import { push } from '@lagunovsky/redux-react-router'
import HomeAction from 'home/actions/HomeAction'
import { orderBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ToastrAction from 'toastr/actions/ToastrAction'
import { H_MAT_CENTRAL } from '../../../../account/constants/AccessRulesConstants'
import FilePanel from '../../../../components/file/FilePanel'
import SmallPicturePanel from '../../../../components/picture/SmallPicturePanel'
import UpdatePanel from '../../../../components/updatePanel/UpdatePanel'
import { PATH_MATERIEL_CENTRAL, PATH_MATERIEL_LINK } from '../../../../home/constants/RouteConstants'
import { componentHasHabilitations } from '../../../../utils/HabilitationUtil'
import { updateMaterialize } from '../../../../utils/MaterializeUtils'
import { getCentralSetting, isSaveBlocked } from '../../../utils/MaterielUtils'
import AdministratorPanel from '../../administrator/AdministratorPanel'
import AssigmentChartPanel from '../../chart/AssigmentChartPanel'
import PowerSupplyAction from '../../powerSupply/actions/PowerSupplyAction'
import LastSituationMarkerPanel from '../../situation/LastSituationMarkerPanel'
import SituationPanel from '../../situation/SituationPanel'
import TelecomAction from '../../telecom/actions/TelecomAction'
import CentralAction from '../actions/CentralAction'
import CentralPanel from './CentralPanel'
import ChannelPanel from './channel/ChannelPanel'
import RangePanel from './range/RangePanel'
import { CENTRAL_ID } from 'materiel/constants/MaterielConstants'
import { useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import { MATERIEL_STATE_IN_STOCK_ID, MATERIEL_STATE_ON_SITE_ID } from 'materiel/constants/MaterielStateConstants'

const CentralApp = ({}) => {
    const [newCentral, setNewCentral] = useState({})
    const [previousCentralChannels, setPreviousCentralChannels] = useState([])
    const [previousCentralSetting, setPreviousCentralSetting] = useState({})
    const [isEditMode, setIsEditMode] = useState(false)

    const {
        central,
        centralChannels,
        centralTypes,
        centralSituations,
        centralRanges,
        centrals,
        centralsLastSituations,
        powerSupplyTypes,
        telecomTypes,
        centralPictures,
        centralFiles,
        accountUser,
        materielSettingRules,
    } = useSelector(store => ({
        central: store.CentralReducer.central,
        centralChannels: store.CentralReducer.centralChannels,
        centralTypes: store.CentralReducer.centralTypes,
        centralSituations: store.CentralReducer.centralSituations,
        centralRanges: store.CentralReducer.centralRanges,
        centrals: store.CentralReducer.centrals,
        centralsLastSituations: store.CentralReducer.centralsLastSituations,
        powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
        telecomTypes: store.TelecomReducer.telecomTypes,
        centralPictures: store.CentralReducer.centralPictures,
        centralFiles: store.CentralReducer.centralFiles,
        accountUser: store.AccountReducer.accountUser,
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }))

    const dispatch = useDispatch()
    const params = useParams()

    const changeAdministrator = administrator => setNewCentral(prev => ({ ...prev, administrator }))

    const changeStateAttribute = changes => setNewCentral(prev => ({ ...prev, ...changes }))

    useActions(() => {
        updateMaterialize()
        if (accountUser.consultant === '1') return {}
        if (params.id === 'new' && (!central || !central.id)) {
            return {
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === CENTRAL_ID)
                    if (isSaveBlocked(centrals, newCentral, rules, 'centralType')) return

                    dispatch(CentralAction.createCentral(newCentral)).then(id => {
                        if (id) {
                            const situation = {
                                idCentral: id,
                                situationDate: newCentral.purchaseDate,
                                statusCode: MATERIEL_STATE_IN_STOCK_ID,
                            }
                            dispatch(CentralAction.saveCentralSituations(situation))
                        }
                    })
                },
                cancel: () => {
                    dispatch(push(`/${PATH_MATERIEL_LINK}${PATH_MATERIEL_CENTRAL}`))
                },
            }
        }
        if (isEditMode) {
            return {
                save: () => {
                    const rules = materielSettingRules.filter(({ idMaterielType }) => idMaterielType === CENTRAL_ID)
                    if (isSaveBlocked(centrals, newCentral, rules, 'centralType')) return
                    dispatch(CentralAction.updateCentral(newCentral)).then(success => success && setIsEditMode(false))
                },
                cancel: () => {
                    setNewCentral(central)
                    setIsEditMode(false)
                },
            }
        }
        return {
            edit: () => {
                setIsEditMode(true)
            },
            delete: () => {
                dispatch(CentralAction.deleteCentral(params.id))
            },
            replace: () => {
                dispatch(ToastrAction.info(i18n.inDeveloppmentFunctionnality))
            },
        }
    }, [isEditMode, newCentral])

    useTitle(() => {
        const title = central.reference || central.serialNumber || central.id
        const type = (centralTypes.find(t => t.code === parseInt(central.centralType)) || {}).label || i18n.notDefined
        return ([
            {
                title: i18n.materiels,
                href: 'materiel',
            },
            {
                title: i18n.centrals,
                href: 'materiel/central',
            },
            {
                title: type,
                href: `materiel/central/${params.id}`,
            },
            {
                title,
                href: `materiel/central/${params.id}`,
            },
        ])
    }, [central, centralTypes])

    useEffect(() => {
        if (!componentHasHabilitations(H_MAT_CENTRAL)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
        }
        if (params.id !== 'new') {
            dispatch(CentralAction.fetchCentral(params.id)).then(json => {
                if (json.id) {
                    dispatch(CentralAction.fetchCentralSituations(json.id))
                    dispatch(CentralAction.fetchCentralRanges(json.id))
                    dispatch(CentralAction.fetchCentralChannels(json.id))
                    dispatch(CentralAction.fetchCentralPictures(json.id))
                    if (json.serialNumber) dispatch(CentralAction.fetchCentralFiles(json.serialNumber))
                    setNewCentral({ ...json })
                }
            })
            setIsEditMode(false)
        } else {
            setIsEditMode(true)
        }
        if (!centrals.length) {
            dispatch(CentralAction.fetchCentrals(true))
        }
        if (!centralTypes.length) {
            dispatch(CentralAction.fetchCentralTypes())
        }
        if (!powerSupplyTypes.length) {
            dispatch(PowerSupplyAction.fetchPowerSupplyTypes())
        }
        if (!telecomTypes.length) {
            dispatch(TelecomAction.fetchTelecomTypes())
        }
        dispatch(HomeAction.setHelpLink('materiel', ''))
        return () => dispatch(CentralAction.resetCentral())
    }, [])

    const lastSituationOnSite = useMemo(() => {
        const lastSituation = orderBy(centralSituations, 'id', 'desc')[0]
        return !!centralSituations.length && !!lastSituation && lastSituation.statusCode === MATERIEL_STATE_ON_SITE_ID
    }, [centralSituations])

    const deleteSituation = id => dispatch(CentralAction.deleteCentralSituation(id, params.id))

    const getMaterielNumber = ({ serialNumber, reference }) => serialNumber || reference || ''

    const getCentralsFormat = () => {
        return centrals.filter(({ isEquipment }) => !isEquipment).map(c => {
            const label = (() => {
                const number = getMaterielNumber(c)
                if (c.centralType) {
                    const type = centralTypes.find(t => t.id === c.centralType)
                    if (type) {
                        return `${type.label} - ${number}`
                    }
                }
                return number
            })()
            return {
                id: c.id,
                label,
            }
        })
    }

    const fetchPreviousParam = previousId => {
        dispatch(CentralAction.fetchPreviousCentral(previousId)).then(json => {
            dispatch(CentralAction.fetchPreviousCentralChannels(json.id)).then(channels => setPreviousCentralChannels(channels))
            setPreviousCentralSetting(getCentralSetting(json))
        })
    }

    const onSavePreviousParam = () => {
        dispatch(CentralAction.updateCentral({ ...central, ...previousCentralSetting }))
        dispatch(CentralAction.updateAllCentralChannel(central.id, previousCentralChannels))
    }

    const disabled = { active: isEditMode, disabled: !isEditMode }

    return (
        <Card>
            <CardContent>
                <Grid container size={12} spacing={2}>
                    <Grid container size={9}>
                        <LastSituationMarkerPanel materielSituation={ centralSituations } />
                        <Grid container size={'grow'}>
                            <Grid size={12}>
                                <CentralPanel
                                    central={ newCentral }
                                    onChange={ changeStateAttribute }
                                    { ...disabled }
                                />
                            </Grid>
                            {lastSituationOnSite ?
                                <>
                                    <Grid size={12}>
                                        <RangePanel
                                            active={ isEditMode }
                                            centralRanges={ centralRanges }
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <ChannelPanel
                                            central={ central }
                                            active={ isEditMode }
                                            centralChannels={ centralChannels }
                                            refreshDataFunction={ () => null }
                                        />
                                    </Grid>
                                </>
                                :
                                undefined
                            }
                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        <Stack spacing={1}>
                            <UpdatePanel
                                updateDate={central.updateDate}
                                updateLogin={central.updateLogin}
                                jobExecId={central.jobExecutionId}
                            />
                            <Card>
                                <CardContent>
                                    <AdministratorPanel
                                        onChange={changeAdministrator}
                                        selectedValue={newCentral.administrator}
                                        isActive={!central.isEquipment && isEditMode}
                                    />
                                </CardContent>
                            </Card>
                            <AssigmentChartPanel materielSituation={centralSituations} />
                            <SmallPicturePanel pictures={centralPictures}/>
                            <FilePanel files={centralFiles} />
                        </Stack>
                    </Grid>
                    {
                        !central.isEquipment && (
                            <SituationPanel
                                materielSituation={centralSituations}
                                active={isEditMode}
                                idMateriel={central.id}
                                serialNumber={central.serialNumber}
                                purchaseDate={central.purchaseDate}
                                idKey={'idCentral'}
                                isCentral={true}
                                lastSituations={centralsLastSituations}
                                fetchPreviousParam={fetchPreviousParam}
                                materials={getCentralsFormat()}
                                saveFunction={(c) => dispatch(CentralAction.saveCentralSituations(c))}
                                savePreviousParam={onSavePreviousParam}
                                deleteFunction={deleteSituation}
                            />
                        )
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}

export default CentralApp
